/**
 * Putting an account under a primary: type the number, see whose account it is,
 * then confirm it before the card is reissued.
 *
 * Two amendments ask this one question. A **downgrade** takes a primary and
 * makes it a supplementary of the primary named; a **change of primary account**
 * takes a supplementary and hangs it off a different one. Either way the clerk
 * types a number that belongs to somebody else and a card is reissued under
 * that account's sequence, so both are asked the same way — as legacy asked
 * them, in two sheets.
 *
 * The first sheet validates: the number is looked up as the typing settles and
 * the name and birthday come back filled in, so what is checked is the account
 * rather than the digits. The second sets the whole account out again and is
 * the only place the amendment is sent from.
 *
 * The verdict is the server's. `GET /members/primary/{number}` is the same
 * function the amendment itself calls, so a number this sheet accepts is one
 * the amendment accepts, and the six reasons an account cannot take another
 * card come back in the server's own words. The three things it cannot know —
 * that the number is the member's own, that it resolves to this very member,
 * that it is the primary the card is already under — are asked here, in
 * legacy's words.
 */

import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { AcquisitionForTheMove, needsAChannel } from "@/components/primary-account-field";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/dialog";
import { Field, Input } from "@/components/ui/field";
import { OptionSelect } from "@/components/ui/option-select";
import { Spinner } from "@/components/ui/spinner";
import { api, errorMessage } from "@/services/api";
import { ACQUISITION, TIER, day, fullName } from "@/pages/MembersPage";
import { useTranslation } from "@/store/locale";
import { atLeast, useRank } from "@/utils/permissions";

/**
 * The gate's answer: a primary that can take another card, as this sheet needs
 * to show it. Reaching this shape at all is the verdict — anything wrong with
 * the account is a 4xx naming which of the six reasons it is.
 */
interface PrimaryAccount {
  member_id: number;
  account_number: string;
  first_name: string;
  middle_name: string | null;
  last_name: string | null;
  date_of_birth: string;
  valid_until: string | null;
  no_expiry: boolean;
  membership_tier: string;
}

/** The two amendments that ask for a primary account number. */
export type PrimaryMove = "downgrade" | "change-primary-account";

/**
 * The tiers a supplementary card can be issued under.
 *
 * The other two have no supplementary identifier range at all — see
 * `SUPPLEMENTARY_RANGE` in the server's numbering, where Member holds 01–49 and
 * Complimentary 51–97 and employee and affiliates hold nothing — so neither can
 * father a supplementary, and neither belongs on a downgrade's tier list.
 */
export const TAKES_SUPPLEMENTARY = new Set(["member", "complimentary"]);

/** Long enough that a ten-digit number is one request, not ten. */
const LOOKUP_DEBOUNCE_MS = 400;

export function PrimaryMoveDialog({
  action,
  memberId,
  accountNumber,
  parentAccountNumber,
  currentAcquisition,
  onClose,
  onDone,
}: {
  action: PrimaryMove;
  memberId: number;
  /** The account's own number: nothing can be its own primary. */
  accountNumber?: string | null;
  /** The primary it hangs off now, when it hangs off one. */
  parentAccountNumber?: string | null;
  /** The channel it is recorded under now; what the move may have to replace. */
  currentAcquisition?: string | null;
  onClose: () => void;
  /** It went through: the page reloads the member behind this. */
  onDone: () => void;
}) {
  const t = useTranslation();
  const rank = useRank();
  // Blank, as legacy opened it. The number the card is under now is on the page
  // behind this sheet, and it is the one answer this modal will not take.
  const [typed, setTyped] = useState("");
  const [number, setNumber] = useState("");
  const [verifying, setVerifying] = useState(false);
  /**
   * The channel the account is recorded under once it has moved.
   *
   * Filled in with the one it already holds, as legacy filled it in — but only
   * when that is a channel a member can have. An account coming off one of the
   * three tiers the club signs up itself carries Benefits, which is not an
   * answer here, so the picker opens empty and waits for a real one. Either
   * way it is the clerk's to change: the card is being issued as a member's,
   * and how that member came in is theirs to say.
   */
  const [movedAs, setMovedAs] = useState(
    currentAcquisition && currentAcquisition !== "benefits" ? currentAcquisition : "",
  );
  /**
   * The tier the card is issued on.
   *
   * The primary's, once one is found, and fixed from that moment: a
   * supplementary takes the tier of the account it hangs off, so a Member
   * primary means a member's card and a Complimentary primary a complimentary
   * one. The list is open before a number is typed, so the two tiers that can
   * hold a supplementary at all are on it and the field reads as the question
   * it is — and the answer, when it comes, comes from the account.
   */
  const [tier, setTier] = useState("");
  // A downgrade is where the account's tier changes hands — it takes the
  // primary's — so that sheet shows the tier and the channel it is about to be
  // recorded under. A move between primaries asks for the account and nothing
  // else, as legacy's did.
  const downgrade = action === "downgrade";

  useEffect(() => {
    const timer = setTimeout(() => setNumber(typed.trim()), LOOKUP_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [typed]);

  const own = (accountNumber ?? "").trim();
  const under = (parentAccountNumber ?? "").trim();
  // Asked before the request rather than after it: the member's own number is
  // an account the server would happily describe, and "Account 5800000600 is
  // active" is not the answer to typing it in here.
  const isSelf = number.length > 0 && number === own;

  // A downgrade files this member under whatever account is named, and the
  // member takes that account's tier, membership and validity with it. Naming a
  // complimentary primary is therefore handing somebody a membership the
  // business gave away, which is a decision above the counter: below Manager a
  // downgrade has to name an ordinary Member primary.
  //
  // Sent as the tier being enrolled so the gate says so while the number is
  // being typed, rather than after Continue and a filled-in form. The server
  // refuses it either way — see `_assert_may_name_this_primary` — this only
  // decides when they find out.
  //
  // Not on `change-primary-account`: moving a supplementary between primaries
  // is not this rule, and the account it moves to is the one it already
  // belongs with.
  const memberOnly = action === "downgrade" && !atLeast(rank, "manager");

  const found = useQuery({
    queryKey: ["members", "primary", number, memberOnly],
    queryFn: async ({ signal }) =>
      (
        await api.get<PrimaryAccount>(`/members/primary/${encodeURIComponent(number)}`, {
          params: memberOnly ? { tier: "member" } : undefined,
          signal,
        })
      ).data,
    enabled: number.length > 0 && !isSelf,
    // A number nobody holds is an answer, not a failure worth retrying.
    retry: false,
    // Never from the cache: this is a verdict about the primary *now* — whether
    // its slots are taken, whether it is still active — not a record.
    staleTime: 0,
    refetchOnMount: "always",
  });
  const parent = found.data ?? null;
  const looking = !isSelf && number.length > 0 && (found.isFetching || typed.trim() !== number);
  // Each account that resolves proposes its own tier: a number retyped is a
  // different family, and the answer from the one before it is not an answer
  // about this one.
  const proposed = parent?.membership_tier;
  useEffect(() => {
    if (proposed) setTier(proposed);
  }, [proposed]);

  /**
   * What is wrong with the number, in one sentence, or nothing.
   *
   * The three the server cannot answer come first — it is the member's own
   * account, it resolves to this very member (a card number reaches the same
   * row), or it is the primary the card already hangs off — and the server's
   * own sentence stands for everything else.
   */
  const problem = isSelf
    ? t("md.primaryIsSelf")
    : parent && parent.member_id === memberId
      ? t("md.primaryIsSameMember")
      : parent && under && parent.account_number === under
        ? t("md.primaryUnchanged")
        : found.isError
          ? errorMessage(found.error, t("new.primaryNotFound"))
          : "";

  /**
   * Whether somebody has to say which channel the account is recorded under.
   *
   * An account takes its tier from the primary it goes under, and the three
   * tiers the club signs up itself settle the channel on their own: Benefits,
   * whatever anyone picks. A Member primary does not, which is where the
   * question comes from — but the two amendments ask it on different terms.
   *
   * A **downgrade** makes the account a member, on a card it did not have
   * before, so the channel is part of that decision and is put to the clerk
   * every time — filled in with the one the account already holds, and theirs
   * to change. Legacy asked it the same way.
   *
   * A **move to another primary** changes nothing about the account but which
   * primary it hangs off, so the channel it already holds stands. Only an
   * account carrying Benefits has to answer, because a member is never
   * acquired that way.
   */
  const channelAsked = downgrade
    ? tier === "member"
    : needsAChannel(currentAcquisition, parent);
  const ready = !!parent && !problem && (!downgrade || !!tier) && (!channelAsked || !!movedAs);

  const move = useMutation({
    mutationFn: (body: { parent_account_number: string; acquisition_type?: string }) =>
      api.post(`/members/${memberId}/amendments/${action}`, body),
    onSuccess: () => {
      toast.success(downgrade ? t("md.amendmentApplied") : t("md.primaryUpdated"));
      onDone();
      onClose();
    },
    // The sheet stays open on a refusal: the number is still in the box, and
    // whatever the server objected to may be a digit away from right.
    onError: (error) => {
      setVerifying(false);
      toast.error(
        errorMessage(error, downgrade ? t("md.amendmentFailed") : t("md.accountChangeFailed")),
      );
    },
  });

  /**
   * One sheet that changes its face, not two sheets that hand over.
   *
   * A native `<dialog>` fires `close` when it is closed, and `Modal` reports
   * that back as `onClose` — so a pair of them, one opening as the other shut,
   * closed this dialog for good on the way to the confirmation: the first
   * sheet's close was read as the clerk dismissing the whole thing. The step is
   * a change of contents, so it is written as one.
   */
  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title={
        verifying
          ? t("new.verifyDetails")
          : downgrade
            ? t("md.downgrade")
            : t("md.changePrimaryTitle")
      }
      description={
        verifying
          ? t("md.verifyPrimaryBody")
          : downgrade
            ? t("md.amend.downgrade")
            : t("md.changePrimaryBody")
      }
      footer={
        verifying ? (
          <>
            <Button variant="secondary" onClick={() => setVerifying(false)}>
              {t("common.back")}
            </Button>
            <Button
              loading={move.isPending}
              onClick={() => {
                if (!parent) return;
                // The channel only travels when the move actually raises the
                // question; sending it otherwise answers one nobody asked.
                move.mutate({
                  parent_account_number: parent.account_number,
                  // Only the channel travels, and only when the move actually
                  // raises the question. The tier is the primary's, which the
                  // server reads for itself off the account named.
                  ...(channelAsked ? { acquisition_type: movedAs } : {}),
                });
              }}
            >
              {t("common.update")}
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button disabled={!ready} onClick={() => setVerifying(true)}>
              {t("login.continue")}
            </Button>
          </>
        )
      }
    >
      {/* A card is about to be reissued under somebody else's account, off a
          number that was typed. That is worth one look before it happens.

          Four rows, the same four on both amendments: whose account this is.
          The tier and the channel are on the sheet behind — one of them a fact
          about the primary and the other an answer already given — and
          repeating them here would make the confirmation a second form rather
          than a look at the account the card is going under. */}
      {verifying ? (
        <dl className="grid gap-3 pb-2">
          {parent
            ? (
                [
                  [t("md.primaryAccountNo"), parent.account_number],
                  [t("mem.name"), fullName(parent)],
                  [t("mem.dateOfBirth"), day(parent.date_of_birth)],
                  [
                    t("mem.validUntil"),
                    parent.no_expiry ? t("mem.noExpiry") : day(parent.valid_until),
                  ],
                ] as const
              ).map(([term, value]) => (
                <div key={term} className="flex items-baseline justify-between gap-4">
                  <dt className="text-fg-muted text-callout">{term}</dt>
                  <dd className="text-fg text-body text-right font-medium">{value}</dd>
                </div>
              ))
            : null}
        </dl>
      ) : (
        <div className="grid gap-4 pb-2">
          <Field
            label={downgrade ? t("new.primaryMember") : t("md.primaryAccountNo")}
            htmlFor="primary-account-no"
            error={problem || undefined}
            // One line under the field for all three answers, so a screen
            // reader hears the verdict change rather than only sighted users
            // seeing the two boxes below it fill in.
            hint={
              looking ? (
                <span className="flex items-center gap-2">
                  <Spinner /> {t("new.lookingUp")}
                </span>
              ) : parent ? (
                t("new.primaryFound")
              ) : undefined
            }
          >
            <Input
              id="primary-account-no"
              autoFocus
              inputMode="numeric"
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              placeholder={t("new.exampleNumber")}
              aria-invalid={problem ? true : undefined}
            />
          </Field>

          {/* Filled from the account, never typed: they are what the clerk
              checks the number against, so they are the account's answer and
              not a second question. Marked required as legacy marked them —
              there is no move without them. */}
          <Field label={t("mem.name")} htmlFor="primary-name" required>
            <Input
              id="primary-name"
              readOnly
              disabled
              value={parent ? fullName(parent) : ""}
              placeholder={t("new.fromAccount")}
            />
          </Field>
          <Field label={t("mem.dateOfBirth")} htmlFor="primary-dob" required>
            <Input
              id="primary-dob"
              readOnly
              disabled
              value={parent ? day(parent.date_of_birth) : ""}
              placeholder={t("new.fromAccount")}
            />
          </Field>

          {/* The tier the card will be issued on: the primary's, and never the
              clerk's to pick. On the sheet from the start rather than
              appearing with the answer, so what is being asked looks the same
              before and after the number is typed — it reads "Select
              Membership Tier" until the account is known and fills itself in
              when it is. */}
          {downgrade ? (
            <Field label={t("mem.membershipTier")} htmlFor="primary-tier" required>
              <OptionSelect
                id="primary-tier"
                value={tier}
                onChange={setTier}
                // Shut once the primary has answered it: the account above
                // decides the tier, and the server writes that tier whatever
                // this said. Open until then, so the field shows what kind of
                // answer is coming.
                disabled={!!parent}
                // A tier is always one of the two; there is no empty tier to go
                // back to once the account is known.
                clearable={false}
                placeholder={t("md.selectTier")}
                choices={TIER(t).filter((option) => TAKES_SUPPLEMENTARY.has(option.value))}
              />
            </Field>
          ) : null}
          {channelAsked ? (
            <AcquisitionForTheMove
              id="change-primary-acquisition"
              value={movedAs}
              onChange={setMovedAs}
              // A downgrade asks for its own sake, not because Benefits has
              // become impossible, so it says why in its own words.
              hint={downgrade ? t("md.channelForTheDowngrade") : undefined}
              current={currentAcquisition}
            />
          ) : downgrade && tier ? (
            <Field label={t("mem.acquisitionType")} htmlFor="primary-acquisition">
              <OptionSelect
                id="primary-acquisition"
                // Complimentary is one of the three tiers the club signs up
                // itself: the card is acquired by benefits and by nothing
                // else, so this says what it will be recorded as rather than
                // asking. The server reads it off the tier the same way.
                value="benefits"
                onChange={() => {}}
                disabled
                clearable={false}
                choices={ACQUISITION(t)}
              />
            </Field>
          ) : null}
        </div>
      )}
    </Modal>
  );
}
